import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Users, ShieldCheck, Leaf, Smile, Check } from 'lucide-react';
import ReusableContactForm from '../../components/ReusableContactForm/ReusableContactForm';
import styles from './AluminumRailings.module.css';

const AluminumRailings = () => {
  return (
    <div className={styles.container}>
      <Helmet>
        <title>Aluminum Railings Edmonton | Sustainable & Code Compliant | ABC Railing</title>
        <meta name="description" content="Premium aluminum railings in Edmonton. Eco-friendly, weather-resistant, Alberta Building Code compliant. Free estimates from Edmonton's trusted railing specialists." />
        <meta name="keywords" content="aluminum railings edmonton, aluminum railing edmonton, weather resistant railings, sustainable railings, Alberta Building Code compliant" />
      </Helmet>

      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1>Aluminum Railings Edmonton</h1>
          <p>Sustainable, Weather-Resistant Railings Built for Alberta's Climate</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#consultation">
              <span>Get Free Estimate</span>
            </a>
            <a href="/architectural" className={styles.secondaryCta}>
              View Our Portfolio
            </a>
          </div>
        </div>
      </section>

      <section className={`${styles.whoWeAreSection} ${styles.featuresSection}`}>
        <div className={styles.sectionContent}>
          <h2>Why Choose ABC Railing for Aluminum Railings in Edmonton?</h2>
          <div className={styles.featuresGrid}>
            <div className={styles.featureItem}>
              <div className={styles.iconContainer}>
                <Users className={styles.featureIcon} />
              </div>
              <h3>Hardworking & Trustworthy Team</h3>
              <p>Our Edmonton team brings over 25 years of combined experience installing aluminum railings across the city. From Sherwood Park to St. Albert, we've built our reputation on showing up on time, staying on budget, and delivering quality that lasts. Every crew member is fully insured and committed to your project's success.</p>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.iconContainer}>
                <ShieldCheck className={styles.featureIcon} />
              </div>
              <h3>Compliance & Knowledge</h3>
              <p>Every aluminum railing installation meets or exceeds Alberta Building Code requirements. We provide engineering stamps when required, and ensure your railings pass inspection the first time. Our knowledge of Edmonton's specific wind and snow loads requirements means your railings are built to withstand our Prairie storms.</p>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.iconContainer}>
                <Leaf className={styles.featureIcon} />
              </div>
              <h3>Environment & Sustainability</h3>
              <p>Our aluminum railings are 100% recyclable and made from sustainable materials. Unlike steel or iron, aluminum never rusts, eliminating the need for harmful chemical treatments. Our powder coating process uses zero VOCs, protecting Edmonton's air quality while delivering a finish that lasts decades in Alberta's harsh climate.</p>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.iconContainer}>
                <Smile className={styles.featureIcon} />
              </div>
              <h3>Cool & Friendly Service</h3>
              <p>No high-pressure sales tactics here. We provide honest, upfront pricing with detailed written estimates. Our friendly consultation process helps you choose the perfect style and color for your Edmonton home. We make what can be a stressful renovation project enjoyable from design to completion.</p>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      <section className={styles.checklistSection}>
        <div className={styles.sectionContent}>
          <h2>Aluminum Railings Features and Benefits</h2>
          <div className={styles.checklistGrid}>
            <div className={styles.checklistItem}>
              <h3>Weather Resistance</h3>
              <ul>
                <li><Check className={styles.checkIcon} /> Withstands Edmonton's -40°C winters without cracking</li>
                <li><Check className={styles.checkIcon} /> UV-resistant powder coating prevents fading</li>
                <li><Check className={styles.checkIcon} /> No rust, rot, or corrosion ever</li>
                <li><Check className={styles.checkIcon} /> Ice and snow slide off easily</li>
              </ul>
            </div>
            <div className={styles.checklistItem}>
              <h3>Style Options</h3>
              <ul>
                <li><Check className={styles.checkIcon} /> Traditional picket designs</li>
                <li><Check className={styles.checkIcon} /> Modern horizontal slat styles</li>
                <li><Check className={styles.checkIcon} /> Glass panel inserts available</li>
                <li><Check className={styles.checkIcon} /> Unlimited powder coat color options</li>
                <li><Check className={styles.checkIcon} /> Custom height configurations</li>
                <li><Check className={styles.checkIcon} /> Decorative post caps and accessories</li>
              </ul>
            </div>
            <div className={styles.checklistItem}>
              <h3>Maintenance Benefits</h3>
              <ul>
                <li><Check className={styles.checkIcon} /> Never needs painting or staining</li>
                <li><Check className={styles.checkIcon} /> Simple soap and water cleaning</li>
                <li><Check className={styles.checkIcon} /> No seasonal maintenance required</li>
                <li><Check className={styles.checkIcon} /> Limited lifetime warranty against manufacturing defects</li>
                <li><Check className={styles.checkIcon} /> 5-year limited warranty on powder coat finish</li>
              </ul>
            </div>
            <div className={styles.checklistItem}>
              <h3>Installation Advantages</h3>
              <ul>
                <li><Check className={styles.checkIcon} /> Lightweight for easier installation</li>
                <li><Check className={styles.checkIcon} /> Pre-engineered components ensure perfect fit</li>
                <li><Check className={styles.checkIcon} /> Faster installation saves you money</li>
                <li><Check className={styles.checkIcon} /> Can be installed year-round in Edmonton</li>
                <li><Check className={styles.checkIcon} /> Minimal disruption to your landscape</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      <section className={styles.processSection}>
        <div className={styles.sectionContent}>
          <h2>Our Edmonton Aluminum Railing Installation Process</h2>
          <p>From consultation to completion, we ensure your aluminum railing project exceeds expectations with professional service and superior craftsmanship.</p>
          
          <div className={styles.processGrid}>
            <div className={styles.processItem}>
              <div className={styles.processNumber}>01</div>
              <h4>Free Consultation</h4>
              <p>We visit your Edmonton property for a no-obligation assessment. We'll discuss styles, colors, and budget while taking precise measurements.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processNumber}>02</div>
              <h4>Custom Design</h4>
              <p>Our team creates detailed drawings showing how your new aluminum railings will look. We'll review materials and ensure Alberta Building Code compliance.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processNumber}>03</div>
              <h4>Engineering</h4>
              <p>We provide engineering stamps if required. No bureaucratic headaches for you.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processNumber}>04</div>
              <h4>Fabrication</h4>
              <p>Your railings are custom fabricated in our Edmonton shop using premium aluminum and the latest welding techniques.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processNumber}>05</div>
              <h4>Professional Installation</h4>
              <p>Our certified installers complete your project efficiently and cleanly. We clean up completely and conduct a final walkthrough.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processNumber}>06</div>
              <h4>Warranty & Support</h4>
              <p>We provide comprehensive warranty documentation and remain available for any questions or future needs.</p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.testimonialsSection}>
        <div className={styles.sectionContent}>
          <h2>Edmonton Homeowners Love Our Work</h2>
          <div className={styles.testimonialsGrid}>
            <div className={styles.testimonial}>
              <p>"Three Edmonton winters and our aluminum railings still look brand new. The team was professional and the quality is outstanding."</p>
              <cite>Sarah M., Riverbend</cite>
            </div>
            <div className={styles.testimonial}>
              <p>"Perfect for our family home. The railings handle Edmonton weather and our kids perfectly. Great investment."</p>
              <cite>Mike & Jennifer L., Sherwood Park</cite>
            </div>
            <div className={styles.testimonial}>
              <p>"ABC Railing handled all permits and code requirements. Two years later, zero maintenance needed. Excellent service."</p>
              <cite>David Chen, Windermere</cite>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.specificationsSection}>
        <div className={styles.sectionContent}>
          <h2>Technical Specifications</h2>
          <div className={styles.specsGrid}>
            <div className={styles.specGroup}>
              <h4>Materials & Construction</h4>
              <ul>
                <li>6061-T6 and 6063-T5 aluminum</li>
                <li>1-3/4" custom extruded posts</li>
                <li>Customizable height</li>
                <li>Powder coated finish</li>
              </ul>
            </div>
            <div className={styles.specGroup}>
              <h4>Performance Standards</h4>
              <ul>
                <li>Temperature range: -50°C to +80°C</li>
                <li>Engineered for Edmonton's wind conditions</li>
                <li>Alberta Building Code compliant</li>
                <li>Child-safe 4" maximum spacing</li>
              </ul>
            </div>
            <div className={styles.specGroup}>
              <h4>Warranty Coverage</h4>
              <ul>
                <li>Limited lifetime structural warranty</li>
                <li>5-year limited finish warranty</li>
                <li>Professional installation guarantee</li>
                <li>Code compliance assurance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      <section className={styles.consultationSection} id="consultation">
        <div className={styles.consultationContent}>
          <div className={styles.consultationText}>
            <h2>Ready for Beautiful, Sustainable Aluminum Railings?</h2>
            <p>Join hundreds of satisfied Edmonton homeowners who chose ABC Railing for their aluminum railing needs. Get your free, no-obligation estimate today and discover why we're Edmonton's most trusted railing specialists.</p>
            <div className={styles.serviceAreas}>
              <p>Serving: Edmonton, Sherwood Park, St. Albert, Spruce Grove, and surrounding areas</p>
            </div>
          </div>
          <div className={styles.contactFormSection}>
            <ReusableContactForm
              title="Get Your Free Estimate"
              compact={true}
              className={styles.consultationContactForm}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AluminumRailings;